import React, { useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Box, useTheme, useMediaQuery, Paper } from "@mui/material";

// Define a component for rendering event content
const EventContent = React.memo(({ eventInfo, isMobile }) => {
  return (
    <Box
      sx={{
        bgcolor: eventInfo.event.extendedProps.clr || "#3788d8",
        color: "#fff",
        overflow: "hidden",
        borderRadius: "4px",
        padding: "2px 4px",
        fontSize: isMobile ? "0.75em" : "0.85em",
        width: "100%"
      }}
      className="fc-daygrid-dot-event"
    >
      <Box className="fc-daygrid-event-dot"></Box>
      <Box className="fc-event-time" sx={{
        fontSize: isMobile ? '0.7em' : '0.8em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {eventInfo.timeText}
      </Box>
      <Box className="fc-event-title" sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: isMobile ? '0.7em' : '0.8em'
      }}>
        {eventInfo.event.title}
      </Box>
    </Box>
  );
});

const DoctorDayView = ({ events, staffUsers, onDateClick, onEventClick, date = null }) => {
  const initialDate = date ? new Date(date) : new Date();
  const [resources, setResources] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Create a memoized renderEventContent function that has access to isMobile
  const memoizedRenderEventContent = React.useCallback((eventInfo) => {
    return <EventContent eventInfo={eventInfo} isMobile={isMobile} />;
  }, [isMobile]);

  // Transform staff users into resources for the calendar
  useEffect(() => {
    if (staffUsers?.length > 0) {
      const doctorResources = staffUsers.map(staff => ({
        id: staff.id.toString(),
        title: `${staff.custom_user.first_name} ${staff.custom_user.last_name}`
      }));
      setResources(doctorResources);
    }
  }, [staffUsers]);

  // Transform events to include resourceId (staff_id) and filter invalid ones
  const transformedEvents = events
    .filter(event => event.staff_id != null) // Exclude events without staff_id
    .map(event => ({
      ...event,
      resourceId: event.staff_id.toString() // Ensure resourceId is a string
    }));

  const validEvents = transformedEvents.filter(event =>
    resources.some(res => res.id === event.resourceId)
  );

  // Responsive settings
  const getCalendarOptions = () => {
    const baseOptions = {
      plugins: [resourceTimeGridPlugin, interactionPlugin],
      initialView: "resourceTimeGridDay",
      resources: resources,
      events: validEvents,
      eventDidMount: (arg) => {
        // Add safety check for event elements
        if (!arg.el) return;
        arg.el.dataset.eventId = arg.event.id;
      },
      dateClick: (info) => {
        // Add resource info to the click event
        const enhancedInfo = {
          ...info,
          resource: info.resource,
          staff_id: info.resource ? info.resource.id : null
        };
        onDateClick(enhancedInfo);
      },
      eventClick: onEventClick,
      eventContent: memoizedRenderEventContent,
      initialDate: initialDate.toISOString().split("T")[0],
      slotDuration: "00:30:00",
      slotLabelInterval: "00:30:00",
      slotLabelFormat: {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      },
      headerToolbar: {
        left: '',
        center: 'title',
        right: 'prev,next today'
      },
      resourceLabelDidMount: (info) => {
        // Customize the doctor column headers
        const el = info.el;
        el.style.fontWeight = 'bold';

        // Make resource labels responsive
        if (isMobile) {
          // For mobile, make the resource labels smaller
          el.style.fontSize = '0.8rem';

          // If name is too long, truncate it
          const nameEl = el.querySelector('.fc-daygrid-day-top');
          if (nameEl) {
            nameEl.style.whiteSpace = 'nowrap';
            nameEl.style.overflow = 'hidden';
            nameEl.style.textOverflow = 'ellipsis';
            nameEl.style.maxWidth = '100px';
          }
        }
      },
      // Enable horizontal scrolling
      resourceAreaWidth: resources.length > 3 ? '150px' : 'auto',
      scrollTime: '08:00:00', // Start the day at 8am
      nowIndicator: true,     // Show the current time indicator
    };

    // Mobile-specific adjustments
    if (isMobile) {
      return {
        ...baseOptions,
        // Adjust time-related formatting for smaller screens
        slotLabelFormat: {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          omitZeroMinute: true
        },
        // Simplified header for mobile
        headerToolbar: {
          left: '',
          center: 'title',
          right: 'prev,next'
        },
        // More compact view for mobile
        resourceAreaWidth: '80px',
        slotMinWidth: '60px',
        // For many resources, allow horizontal scrolling
        resourcesInitiallyExpanded: false,
        forceEventDuration: true
      };
    }

    return baseOptions;
  };

  return (
    <Box sx={{
      width: '100%',
      '& .fc': {
        // Base styles for the calendar
        fontSize: isMobile ? '0.8rem' : '1rem',
        maxWidth: '100%',
      },
      '& .fc-timegrid-slot': {
        height: isMobile ? '35px' : '50px',
      },
      '& .fc-toolbar-title': {
        fontSize: isMobile ? '1.2rem' : '1.5rem',
      },
      '& .fc-button': {
        padding: isMobile ? '0.2rem 0.5rem' : '0.4rem 0.65rem',
        fontSize: isMobile ? '0.8rem' : '1rem',
      },
      '& .fc-resource-group': {
        fontSize: isMobile ? '0.75rem' : '0.9rem',
      },
      // Add horizontal scrolling for the calendar
      '& .fc-scroller': {
        overflow: 'auto !important',
      },
      // Make sure resource columns have a minimum width
      '& .fc-resource-timeline-divider': {
        width: '1px'
      },
      // Ensure the time grid can scroll horizontally when many resources
      '& .fc-timegrid': {
        minWidth: resources.length > 3 ? `${resources.length * 150}px` : 'auto',
        maxWidth: '100%',
      },
      // Ensure resource columns don't get too narrow
      '& .fc-timegrid-col': {
        minWidth: isMobile ? '120px' : '150px',
      }
    }}>
      <Paper elevation={2} sx={{
        p: 1,
        borderRadius: 2,
        overflow: 'auto',
        maxWidth: '100%'
      }}>
        <FullCalendar {...getCalendarOptions()} />
      </Paper>
    </Box>
  );
};

export default DoctorDayView;